import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'
import { selectUserToken } from '../store/user'

import { conversationClient } from '../clients/ConversationClient'

import { Sms, Conversation } from '../models/client/conversations'
import { CheckinSms } from '../models/client/checkins'
import { translateCheckinToBoardData } from '../clients/tools'

type Return = {
    isSmsLoading: boolean
    messages: Sms[]
    initialMessages: Sms[]
    setMessages: (data: Sms[]) => void
    refreshSms: boolean
    setRefreshSms: (data: boolean) => void
    setConversationId: (data: string) => void
    conversationId: string
    sendCheckinSms: (values: CheckinSms) => Promise<any>
}

type Params = {
    conversationId: string
}

export const useConversations = (): Return => {
    const [refresh, setRefresh] = useState(false)
    const [isSmsSuccess, setSmsSuccess] = useState(false)
    const [intervalMs, setIntervalMs] = useState(5000)
    const token = useSelector(selectUserToken, shallowEqual)
    const [conversationId, setConversationId] = useState(null)
    const [messages, setMessages] = useState([])

    const SMS_LIST_QUERY_KEY = useMemo(
        () => ['getConversationSms', token, conversationId],
        [token, conversationId]
    )

    const { data: smsMessages, status: smsListStatus } = useQuery(
        SMS_LIST_QUERY_KEY,
        () => {
            if (!token || !conversationId) return []
            return conversationClient.getConversationSms({ token, conversationId })
        },
        {
            enabled: true,
            refetchOnWindowFocus: true,
            keepPreviousData: true,
            refetchInterval: intervalMs,
            notifyOnChangeProps: ['data', 'error'],
        }
    )

    const { mutateAsync: sendSms, status: sendStatus } = useMutation(
        conversationClient.sendCheckinSms,
        {
            onSuccess: (data) => {
                return setSmsSuccess(data?.status)
            },
        }
    )
    const initialMessages = useMemo(() => smsMessages, [smsMessages, refresh])

    const isLoading = smsListStatus === 'loading' || sendStatus === 'loading'

    return {
        isSmsLoading: isLoading,
        messages,
        initialMessages,
        setMessages,
        refreshSms: refresh,
        setRefreshSms: setRefresh,
        conversationId,
        setConversationId,
        async sendCheckinSms(values) {
            if (!token) return false
            try {
                await sendSms({ token, ...values })
            } catch (e) {
                if (e instanceof Error) {
                    throw new Error('err')
                }
            }
        },
    }
}
